import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { DetailsCell, DurationCell, ReferrerCell, VisitorDetailsCell } from '@va/dashboard/components';
import { useTranslate } from '@va/localization';
import { RecordingType } from '@va/types/recordings';
import { HeaderCellV8 } from '@va/ui/components/data-table';
import { ButtonColors } from '@va/ui/design-system';
import { isWixMiniApp } from '@va/util/helpers';
import { DashboardAppWorker } from '@va/util/misc';
import { useMemo } from 'react';
import { useHistory } from 'react-router';
import { ActionsCell, IdAndDateCell, PagesCell } from './cells';
import { ABECell } from './cells/ABECell';
import { calculateTotalAlarmingEvents } from './helpers';
import { VisitorAlarmingActionCount } from './VisitorAlarmingActionCount';

type ColumnStyle = {
  squareIcons?: boolean;
  buttonColor?: ButtonColors;
  recordingsIconColor?: string;
  recordingsIconWrapper?: string;
  size?: 'small' | 'medium';
};

const columnStyles: Record<'default' | 'wix-mini', ColumnStyle> = {
  default: {},
  'wix-mini': {
    recordingsIconColor: 'var(--color-primary)',
    squareIcons: true,
    buttonColor: 'secondary',
    recordingsIconWrapper: 'bg-primary-lighter-06!',
    size: 'small',
  },
};

const columnHelper = createColumnHelper<RecordingType>();

export const useRecordingsColumnsV8 = (variant: 'default' | 'wix-mini' = 'default') => {
  const history = useHistory();
  const translate = useTranslate();

  const visitorActionsColumn = useMemo(
    () =>
      columnHelper.accessor('alarmingEvents', {
        minSize: 120,
        id: 'abes',
        enableSorting: true,
        header: (ctx) => {
          const data = ctx.table.getRowModel().rows.map((row) => row.original);
          const totalAlarmingEvents = calculateTotalAlarmingEvents(data);
          return (
            <HeaderCellV8
              context={ctx}
              text={translate('tableHeaders.visitorActions')}
              tooltip={translate('all.recordings.table.headerTooltips.visitorActions')}
              additionalInfo={<VisitorAlarmingActionCount count={totalAlarmingEvents} />}
            />
          );
        },
        cell: (props) => {
          const { alarmingEvents } = props.row.original;
          return <ABECell alarmingEvents={alarmingEvents} variant={variant} />;
        },
      }),
    [translate, variant],
  );

  // TODO Always check WiX Mini App when altering minSize values
  const allColumns = useMemo<ColumnDef<RecordingType, any>[]>(
    () => [
      columnHelper.accessor('sessionStart', {
        id: 'createdAt',
        enableSorting: true,
        minSize: 200,
        header: (ctx) => (
          <HeaderCellV8
            context={ctx}
            text={translate('tableHeaders.recordingIdAndDate')}
            tooltip={translate('all.recordings.table.headerTooltips.idAndDate')}
            size={columnStyles[variant].size}
            className='justify-start!'
          />
        ),
        cell: (props) => {
          const {
            id,
            sessionStart,
            sessionExtras: { star, seen },
            comment,
          } = props.row.original;
          return (
            <IdAndDateCell
              note={comment?.text}
              recordingId={id}
              date={sessionStart}
              star={star}
              seen={seen}
              iconColor={columnStyles[variant].recordingsIconColor}
              iconWrapperClassName={columnStyles[variant].recordingsIconWrapper}
              size={columnStyles[variant].size}
              onTitleClick={(recordingId, event) => {
                // VISA-11501 - track click on view recording
                if (isWixMiniApp()) {
                  DashboardAppWorker.trackEvent({
                    category: 'Recording Main View',
                    action: 'onclick',
                    label: 'View Recording via ID Label',
                    value: 'Button',
                  });
                }

                event.stopPropagation();
                history.push(`/behaviour/recordings/${recordingId}`, { from: history.location.pathname });
              }}
            />
          );
        },
      }),
      columnHelper.accessor('ssrInfo.duration', {
        id: 'duration',
        enableSorting: true,
        minSize: 90,
        header: (ctx) => (
          <HeaderCellV8
            context={ctx}
            text={translate('tableHeaders.recordingDuration')}
            tooltip={translate('all.recordings.table.headerTooltips.recordingDuration')}
            size={columnStyles[variant].size}
          />
        ),
        cell: (props) => <DurationCell duration={props.getValue()} size={columnStyles[variant].size} />,
      }),
      columnHelper.accessor('ssrInfo.pagesCount', {
        id: 'pages',
        enableSorting: true,
        minSize: 190,
        header: (ctx) => (
          <HeaderCellV8
            context={ctx}
            text={translate('tableHeaders.pages')}
            tooltip={translate('all.recordings.table.headerTooltips.pages')}
            size={columnStyles[variant].size}
          />
        ),
        cell: (props) => {
          const {
            entryPage,
            exitPage,
            ssrInfo: { pagesCount, pages },
            countryCode,
            id,
          } = props.row.original;

          return (
            <PagesCell
              sessionId={id}
              entryPage={entryPage}
              exitPage={exitPage}
              pages={pages}
              totalCount={pagesCount}
              countryCode={countryCode}
              size={columnStyles[variant].size}
              isClickable={variant === 'default'}
            />
          );
        },
      }),
      columnHelper.group({
        id: 'visitorDetails',
        minSize: 140,
        header: (ctx) => (
          <HeaderCellV8
            context={ctx}
            text={translate('tableHeaders.visitorDetails')}
            tooltip={translate('all.recordings.table.headerTooltips.visitorDetails')}
            size={columnStyles[variant].size}
          />
        ),
        cell: (props) => {
          const { status, countryCode, location, id } = props.row.original;
          return (
            <VisitorDetailsCell
              sessionId={id}
              location={location}
              visitorType={status}
              countryCode={countryCode}
              size={columnStyles[variant].size}
              squareIcon={columnStyles[variant].squareIcons}
              isClickable={variant === 'default'}
            />
          );
        },
      }),
      columnHelper.accessor('referrer', {
        minSize: 160,
        header: (ctx) => (
          <HeaderCellV8
            context={ctx}
            text={translate('tableHeaders.referrer')}
            tooltip={translate('all.recordings.table.headerTooltips.referrer')}
            size={columnStyles[variant].size}
          />
        ),
        cell: (props) => (
          <ReferrerCell
            referrer={props.getValue()}
            size={columnStyles[variant].size}
            squareIcon={columnStyles[variant].squareIcons}
          />
        ),
      }),
      columnHelper.group({
        id: 'details',
        minSize: 160,
        header: (ctx) => (
          <HeaderCellV8
            context={ctx}
            text={translate('tableHeaders.recordingDetails')}
            tooltip={translate('all.recordings.table.headerTooltips.recordingDetails')}
            size={columnStyles[variant].size}
          />
        ),
        cell: (props) => {
          const { browser, platform, device } = props.row.original;
          return (
            <DetailsCell
              browser={browser}
              platform={platform}
              device={device}
              size={columnStyles[variant].size}
              squareIcon={columnStyles[variant].squareIcons}
            />
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        minSize: 100,
        header: (ctx) => (
          <HeaderCellV8
            context={ctx}
            text={translate('tableHeaders.actions')}
            tooltip={translate('all.recordings.table.headerTooltips.actions')}
            size={columnStyles[variant].size}
          />
        ),
        cell: (props) => {
          const {
            id,
            comment,
            sessionExtras: { seen, star },
          } = props.row.original;
          return (
            <ActionsCell
              seenButtonColor={columnStyles[variant].buttonColor}
              star={star}
              hasComment={!!comment}
              recordingId={id}
              seen={seen}
              size={columnStyles[variant].size}
            />
          );
        },
      }),
    ],
    [history, translate, variant],
  );

  return useMemo(() => {
    if (!isWixMiniApp()) {
      allColumns.splice(2, 0, visitorActionsColumn);
    }

    if (isWixMiniApp()) {
      allColumns.splice(1, 0, visitorActionsColumn);
    }

    return allColumns;
  }, [allColumns, visitorActionsColumn]);
};
